import { useState, useEffect } from 'react';
import { useAuth } from './useAuth';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface SecurityDashboardStats {
  total_logins: number;
  unique_countries: number;
  unique_cities: number;
  suspicious_logins: number;
  last_login: string | null;
  most_common_country: string;
  most_common_city: string;
  security_score: number;
  security_level: 'Excellent' | 'Good' | 'Fair' | 'Needs Attention';
}

export interface LoginHistoryEntry {
  id: string;
  country: string;
  city: string;
  login_time: string;
  is_suspicious: boolean;
  user_agent: string;
  latitude: number | null;
  longitude: number | null;
}

export const useSecurityDashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState<SecurityDashboardStats | null>(null);
  const [history, setHistory] = useState<LoginHistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDashboardData = async () => {
    if (!user) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Fetch stats
      const { data: statsData, error: statsError } = await supabase.rpc(
        'get_user_security_dashboard' as any,
        { _user_id: user.id }
      );

      if (statsError) {
        console.error('Error fetching dashboard stats:', statsError);
        throw new Error('Failed to load security statistics');
      }

      setStats(statsData as any);

      // Fetch login history
      const { data: historyData, error: historyError } = await supabase.rpc(
        'get_user_login_history' as any,
        { _user_id: user.id, _limit: 50, _offset: 0 }
      );

      if (historyError) {
        console.error('Error fetching login history:', historyError);
        throw new Error('Failed to load login history');
      }

      setHistory((historyData || []) as any);
    } catch (err: any) {
      console.error('Failed to fetch security dashboard:', err);
      setError(err.message || 'Failed to load security dashboard');
      toast.error('Failed to load security dashboard');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, [user]);

  return { 
    stats, 
    history, 
    loading, 
    error,
    refresh: fetchDashboardData 
  };
};
